import { useState, useRef, useEffect } from "react";
import { Input } from "../ui/input";
import { Text } from "../ui/text";
import ChatMoreMenu from "./chat-more-menu";
import { overlay } from "overlay-kit";
import ChatShareDialog from "./chat-share-dialog";
import { useDeleteChatMutation } from "@/hooks/services/mutations/ai-agent/use-delete-chat-mutation";
import { useUpdateChatMutation } from "@/hooks/services/mutations/ai-agent/use-update-chat-mutation";
import { Link, useLocation } from "react-router-dom";
import { useOutsideClick } from "@/hooks/shared/use-outside-click";
import { AI_CHAT_PATHNAME } from "@/constants/pathname";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";

export interface ChatItem {
  chatId: string;
  chatTitle: string;
  updatedAt: string;
}

interface ChatItemProps {
  chatItem: ChatItem;
}

const ChatItem = ({ chatItem }: ChatItemProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(chatItem.chatTitle);
  const inputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();

  const { mutate: deleteChat } = useDeleteChatMutation();
  const { mutate: updateChat } = useUpdateChatMutation();

  // 현재 선택된 채팅인지 확인 (URL pathname에서 chatId 추출)
  const currentChatId = location.pathname.split('/').pop();
  const isSelected = currentChatId === chatItem.chatId;

  // chatTitle props가 바뀌면 (백엔드 제목 생성 후 캐시 업데이트) 로컬 title도 동기화
  // 편집 중일 때는 사용자 입력을 덮어쓰지 않도록 skip
  useEffect(() => {
    if (!isEditing) {
      setTitle(chatItem.chatTitle);
    }
  }, [chatItem.chatTitle, isEditing]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      const trimmedTitle = title.trim();
      
      if (trimmedTitle === "") {
        return;
      }
    
      setTitle(trimmedTitle);
      setIsEditing(false);
      updateChat({ chatId: chatItem.chatId, title });
    }
  };

  const handleDeleteChat = () => {
    deleteChat(chatItem.chatId);
    setIsEditing(false);
  };

  const containerRef = useOutsideClick(() => setIsEditing(false));

  return (
    <li>
      <Link
        to={`${AI_CHAT_PATHNAME}/${chatItem.chatId}`}
        className="flex justify-between items-center group"
      >
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className="w-[140px] truncate overflow-hidden whitespace-nowrap"
                ref={containerRef}
              >
                {!isEditing ? (
                  <Text
                    variant={"label-lg"}
                    className={isSelected ? "text-primary-20" : "text-grey-50"}
                  >
                    {chatItem.chatTitle}
                  </Text>
                ) : (
                  <Input
                    ref={inputRef}
                    className="h-[20px] bg-primary-100 rounded-none w-full px-0"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    onKeyUp={handleKeyUp}
                  />
                )}
              </div>
            </TooltipTrigger>
            {!isEditing && (
              <TooltipContent
                side="right"
                sideOffset={4}
                className="max-w-[290px] whitespace-normal break-words text-grey-20"
              >
                {chatItem.chatTitle}
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
        {!isEditing && (
          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
            <ChatMoreMenu
              onShareClick={() =>
                overlay.open(({ isOpen, close }) => (
                  <ChatShareDialog
                    isOpen={isOpen}
                    onClose={() => close()}
                    chatId={chatItem.chatId}
                  />
                ))
              }
              onRenameClick={() => setIsEditing(true)}
              onDeleteClick={handleDeleteChat}
            />
          </div>
        )}
      </Link>
    </li>
  );
};

export default ChatItem;
